// Helpers for the search box shown on long Scheduler filter columns
import { deburr, identity } from 'lodash';

import type { FilterField } from '@/context/SchedulerContext';

// Short fixed lists that never get a search box, however they render
const NON_SEARCHABLE_FILTER_FIELDS: readonly FilterField[] = [
  'stage',
  'overallStatus',
];

// Fields whose search box is only useful to ISD Support users, who see
// options across every company (other users only see a handful)
const SUPPORT_ONLY_SEARCHABLE_FILTER_FIELDS: readonly FilterField[] = [
  'brokerCompanyRef',
];

/**
 * Whether a filter column may show a search box when its options overflow.
 * Stage and Status never do; Broker only does for ISD Support users.
 *
 * @param {FilterField} filterField - The filter column's field.
 * @param {Object} options
 * @param {boolean} options.isSupportUser - Whether the user is ISD Support.
 * @returns {boolean} True if the column may show a search box.
 */
export function canSearchFilterField(
  filterField: FilterField,
  { isSupportUser }: { isSupportUser: boolean }
): boolean {
  if (NON_SEARCHABLE_FILTER_FIELDS.includes(filterField)) {
    return false;
  }
  if (SUPPORT_ONLY_SEARCHABLE_FILTER_FIELDS.includes(filterField)) {
    return isSupportUser;
  }
  return true;
}

/**
 * Normalizes text for search matching: lowercase, without diacritics and
 * with whitespace trimmed and collapsed, so "jose" matches "José" and
 * "big  rock" matches "Big Rock".
 *
 * @param {string} text - Text to normalize.
 * @returns {string} The normalized text.
 */
function normalizeSearchText(text: string): string {
  return deburr(text).trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Narrows a filter column's options to those matching a search query,
 * keeping their order. Matching is a case- and accent-insensitive substring
 * match against both the raw value and its displayed label. Only matches
 * are returned: selected options that don't match are hidden while
 * searching but stay selected. A blank query returns every option.
 *
 * @param {string[]} options - The column's options.
 * @param {string} query - The text typed in the search box.
 * @param {Object} [config]
 * @param {Function} [config.formatOption] - Formats an option for display.
 * @returns {string[]} The matching options.
 */
export function filterOptionsBySearch(
  options: string[],
  query: string,
  {
    formatOption = identity,
  }: {
    formatOption?: (option: string) => string;
  } = {}
): string[] {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) {
    return options;
  }
  return options.filter((option) => {
    if (normalizeSearchText(option).includes(normalizedQuery)) {
      return true;
    }
    // Only check the label when formatting actually changes the value
    const label = formatOption(option);
    return (
      label !== option && normalizeSearchText(label).includes(normalizedQuery)
    );
  });
}
